<!--
 * @Descripttion: 星盘页「相位搜索」弹窗（和谐相位搜索）
 *   借鉴老系统（web/yljx HomeController@search2）的规则：给定日期区间与「和谐相位」阈值，
 *   逐日取十颗行星黄经，判定 60°±9°（六分相 *）、120°±9°（三分相 △）与「木土会局」，
 *   命中项数 ≥ 阈值 的日期即「可用日期」，结果以「日期 | 相位」表格呈现。
 *
 *   数据来源改为新系统：星盘 astrolabe_micronaut 的免鉴权接口
 *     - GET /api/planet-longitudes?date=&hour=1&timezone=8  （每天一次，取十颗行星黄经）
 *     - GET /api/eclipse/next-solar|next-lunar              （区间起点之后最近一次日月食，用于标记）
 *   计算全部在浏览器端完成，不依赖官网 Java 后端的 /yljx/public/search2。
 *
 *   日期范围（2026-10-04 调整）：起止日期各自独立、可自由调整 ——
 *     - 手动输入或日历点选都行，**不限可选范围**（过去 / 未来都可，已去掉原「不能选未来日期」的限制）；
 *     - 日历年月表头可跨月、跨年切换，区间可任意跨月跨年；
 *     - 默认初值为「今天起一周」，只在组件创建时算一次，用户改完不再重置；
 *     - 若出现「结束早于开始」，搜索时自动交换后执行，保证所选区间一定被正确应用；
 *     - 桌面端弹窗仍为 760px，移动端（<768px）收窄为 94% 且日期控件占满整行。
 *
 * @params:
 *   visible [Boolean] 显示控制（配合 .sync 使用）
 * @Date: 2026-10-04
-->
<template>
  <!-- append-to-body 必须保留：星盘页存在 `#xxx > * { position: relative }` 通配规则，
       不挂到 body 时弹窗会掉进文档流并失去遮罩关闭 -->
  <el-dialog
    title="搜索结果"
    :visible.sync="dialogVisible"
    :width="dialogWidth"
    append-to-body
    :close-on-click-modal="false">
    <div class="phase-search-bar">
      <span class="phase-search-label">日期范围:</span>
      <!-- 两个日期各自独立、可自由调整：手动输入或日历点选，不限可选范围（过去 / 未来都可），
           日历年月表头支持跨月、跨年切换，不设 disabledDate -->
      <el-date-picker
        v-model="dateRange[0]"
        class="phase-date-input"
        type="date"
        editable
        clearable
        format="yyyy-MM-dd"
        value-format="yyyy-MM-dd"
        placeholder="开始日期">
      </el-date-picker>
      <el-date-picker
        v-model="dateRange[1]"
        class="phase-date-input"
        type="date"
        editable
        clearable
        format="yyyy-MM-dd"
        value-format="yyyy-MM-dd"
        placeholder="结束日期">
      </el-date-picker>
      <span class="phase-search-label">和谐相位:</span>
      <el-input-number
        v-model="harmoniousRange"
        controls-position="right"
        :min="3"
        :max="20"
        :step="1">
      </el-input-number>
      <el-button type="primary" :loading="loading" @click="search">搜索</el-button>
    </div>

    <div class="phase-search-tip">
      红色日期为日月食当天；相位串中 <b>*</b> 为六分相（60°）、<b>△</b> 为三分相（120°）。
      单次最多搜索 {{ maxSearchDays }} 天。
    </div>

    <div v-if="loading" class="phase-search-progress">
      正在计算 {{ progressDone }} / {{ progressTotal }} 天…
    </div>

    <el-table
      v-loading="loading"
      :data="rows"
      style="width: 100%"
      height="360"
      :empty-text="emptyText">
      <el-table-column prop="a" label="日期" width="150"></el-table-column>
      <el-table-column prop="b" label="相位"></el-table-column>
    </el-table>

    <div slot="footer" class="dialog-footer">
      <span class="phase-search-count">共 {{ rows.length }} 天命中</span>
      <el-button @click="dialogVisible = false">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { Message } from 'element-ui'
import api from '../../services/api'
import PermissionService from '../../services/PermissionService'

const harmonyPhaseSearch = require('../../utils/harmonyPhaseSearch')

/** 老版 $ihour = 1：每天取北京时间 01:00 计算 */
const CALC_HOUR = 1
/** 老版用东八区 */
const BEIJING_TIMEZONE = 8
/** 逐日调用星盘接口的并发数：克制取值，避免一次性打满浏览器连接与星盘服务 */
const FETCH_CONCURRENCY = 4
/**
 * 日期范围的默认初值：今天起一周（含今天共 7 天）。
 * 只作为首次的合理默认值 —— 用户改动后一律沿用，弹窗开关、重复搜索都不重置。
 */
const DEFAULT_RANGE_DAYS = 7
/** 视口小于该宽度按移动端处理：弹窗改百分比宽度、日期输入框占满整行 */
const MOBILE_BREAKPOINT = 768
/** 桌面端弹窗宽度（与原有视觉一致） */
const DESKTOP_DIALOG_WIDTH = '760px'
/** 移动端弹窗宽度：留出左右边距，避免 760px 在手机上溢出 */
const MOBILE_DIALOG_WIDTH = '94%'

function formatLocalDate (date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return date.getFullYear() + '-' + month + '-' + day
}

function addDays (date, days) {
  const copy = new Date(date.getTime())
  copy.setDate(copy.getDate() + days)
  return copy
}

export default {
  name: 'PhaseSearchDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    // 默认区间：今天 ~ 今天+6（一周），只在组件创建时算一次。
    // 之后弹窗反复开关、连续搜索都不再重置，保证「用户改完就是改完了」。
    const today = new Date()
    return {
      dateRange: [
        formatLocalDate(today),
        formatLocalDate(addDays(today, DEFAULT_RANGE_DAYS - 1))
      ],
      harmoniousRange: 3,
      loading: false,
      progressDone: 0,
      progressTotal: 0,
      rows: [],
      searched: false,
      maxSearchDays: harmonyPhaseSearch.MAX_SEARCH_DAYS,
      // 视口宽度：仅用于决定弹窗宽度；日期可选范围不再受任何限制
      windowWidth: typeof window === 'undefined' ? MOBILE_BREAKPOINT : window.innerWidth
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (value) {
        this.$emit('update:visible', value)
      }
    },
    emptyText () {
      if (this.loading) return '正在搜索…'
      return this.searched ? '该区间内没有满足条件的日期' : '请选择日期范围后点击「搜索」'
    },
    /** 弹窗宽度：桌面 760px（与原视觉一致），移动端收窄为百分比，避免溢出 */
    dialogWidth () {
      return this.windowWidth < MOBILE_BREAKPOINT ? MOBILE_DIALOG_WIDTH : DESKTOP_DIALOG_WIDTH
    }
  },
  mounted () {
    window.addEventListener('resize', this.handleViewportResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleViewportResize)
  },
  methods: {
    /** 仅记录视口宽度，用于移动端切换弹窗宽度（不触碰用户已选的日期） */
    handleViewportResize () {
      this.windowWidth = window.innerWidth
    },
    async search () {
      if (this.loading) return

      // 弹窗级防御性判权：即使绕过顶部入口被程序化打开，也不能发起搜索。
      // （权限来源：星盘 bootstrap 依据 users.hexie 下发的 harmonious_phase_search）
      if (!PermissionService.hasPermission('harmonious_phase_search')) {
        Message.warning('该功能需先开通「和谐相位搜索」')
        this.dialogVisible = false
        return
      }

      let start = this.dateRange[0]
      let end = this.dateRange[1]
      if (!start || !end) {
        Message.warning('请先选择开始日期与结束日期')
        return
      }
      // 两个日期各自独立可调，允许出现「结束早于开始」的中间状态。
      // 这里按「早→晚」自动纠正后再搜索，而不是直接报错，避免用户改完日期还要自己去对齐顺序。
      // value-format 固定为 yyyy-MM-dd，字符串比较即时间先后比较。
      if (start > end) {
        const swapped = [end, start]
        start = swapped[0]
        end = swapped[1]
        this.dateRange = swapped
        Message.info('开始日期晚于结束日期，已自动交换后搜索')
      }
      const dates = harmonyPhaseSearch.enumerateDates(start, end)
      if (dates.length === 0) {
        Message.warning('日期格式不正确，请重新选择开始日期与结束日期')
        return
      }
      if (dates.length > harmonyPhaseSearch.MAX_SEARCH_DAYS) {
        Message.warning('日期范围过大：单次最多 ' + harmonyPhaseSearch.MAX_SEARCH_DAYS + ' 天，请缩小范围后重试')
        return
      }

      this.loading = true
      this.rows = []
      this.searched = true
      this.progressDone = 0
      this.progressTotal = dates.length

      try {
        // 日月食标记只与区间起点有关（老版以起点日北京时间 01:00 为基准取之后最近一次全食）
        const solarEclipseDate = harmonyPhaseSearch.eclipseDateFromResponse(
          await api.getNextSolarEclipse(start, CALC_HOUR, BEIJING_TIMEZONE)
        )
        const lunarEclipseDate = harmonyPhaseSearch.eclipseDateFromResponse(
          await api.getNextLunarEclipse(start, CALC_HOUR, BEIJING_TIMEZONE)
        )

        const samples = await this.fetchDaySamples(dates)
        this.rows = harmonyPhaseSearch.searchHarmonyRows(samples, this.harmoniousRange, solarEclipseDate, lunarEclipseDate)

        if (this.rows.length === 0) {
          Message.info('该区间内没有满足条件的日期，可调低「和谐相位」数值后重试')
        }
      } catch (error) {
        const reason = error && error.message ? error.message : String(error)
        Message.error('相位搜索失败：' + reason)
      } finally {
        this.loading = false
      }
    },

    /**
     * 逐日取十颗行星黄经（限并发）。任一天取数失败直接抛出，避免结果比老系统「少几天」而无感知。
     * @param {String[]} dates yyyy-MM-dd 数组
     * @returns {Promise<Array<{date: String, longitudes: Number[]}>>} 与 dates 同序
     */
    async fetchDaySamples (dates) {
      const samples = new Array(dates.length)
      let cursor = 0

      const worker = async () => {
        while (cursor < dates.length) {
          const index = cursor
          cursor += 1
          const date = dates[index]
          const response = await api.getPlanetLongitudes(date, CALC_HOUR, BEIJING_TIMEZONE)
          const parsed = harmonyPhaseSearch.planetLongitudesFromResponse(response)
          if (parsed.missing.length > 0) {
            throw new Error('星盘未返回行星黄经（' + date + '，缺序号 ' + parsed.missing.join(' ') + '）')
          }
          samples[index] = { date, longitudes: parsed.longitudes }
          this.progressDone += 1
        }
      }

      const workers = []
      const workerCount = Math.min(FETCH_CONCURRENCY, dates.length)
      for (let i = 0; i < workerCount; i++) {
        workers.push(worker())
      }
      await Promise.all(workers)
      return samples
    }
  }
}
</script>

<style scoped>
.phase-search-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

/* 日期输入框：桌面端与「和谐相位」输入框、搜索按钮同排；宽度用类而不用内联样式，
   便于移动端用媒体查询覆盖（优先级高于 Element 默认的 .el-date-editor.el-input 220px） */
.phase-search-bar .phase-date-input {
  width: 150px;
}

.phase-search-label {
  margin-right: 8px;
  font-size: 13px;
  color: #606266;
}

.phase-search-bar .phase-search-label:not(:first-child) {
  margin-left: 15px;
}

.phase-search-bar .el-button {
  margin-left: 15px;
}

.phase-search-tip {
  margin: 12px 0;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}

.phase-search-progress {
  margin-bottom: 8px;
  font-size: 13px;
  color: #409eff;
}

.phase-search-count {
  float: left;
  line-height: 32px;
  font-size: 13px;
  color: #909399;
}

/* 移动端：日期控件占满整行、搜索条纵向排列。
   Element 的日历面板宽约 322px，输入框左对齐贴边才能在窄屏完整显示；
   弹窗宽度由 dialogWidth 计算属性收窄为 94%（见 MOBILE_BREAKPOINT）。 */
@media (max-width: 768px) {
  .phase-search-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .phase-search-bar .phase-search-label {
    margin: 10px 0 4px;
  }

  .phase-search-bar .phase-search-label:first-child {
    margin-top: 0;
  }

  .phase-search-bar .phase-search-label:not(:first-child) {
    margin-left: 0;
  }

  .phase-search-bar .phase-date-input {
    width: 100%;
  }

  .phase-search-bar .el-input-number {
    width: 100%;
  }

  .phase-search-bar .el-button {
    margin-left: 0;
    margin-top: 12px;
  }
}
</style>
